/**
 * useFloorPlanImage Hook
 *
 * Custom hook that wraps the use-image library for loading floor plan images
 * with Konva. Provides convenient loading states and image dimensions.
 *
 * @example
 * ```tsx
 * const { image, isLoading, hasError, naturalWidth, naturalHeight } =
 *   useFloorPlanImage(floorPlan.imageUrl);
 *
 * if (isLoading) return <LoadingSpinner />;
 * if (hasError) return <ErrorMessage>Failed to load floor plan</ErrorMessage>;
 *
 * return (
 *   <Stage width={naturalWidth} height={naturalHeight}>
 *     <Layer>
 *       <KonvaImage image={image} />
 *     </Layer>
 *   </Stage>
 * );
 * ```
 */

import { useEffect, useRef } from 'react';
import useImage from 'use-image';
import { perfStart, perfEnd } from '@/utils/performanceMonitor';

/**
 * Image loading status
 */
export type ImageLoadingStatus = 'loading' | 'loaded' | 'failed';

/**
 * Options for useFloorPlanImage hook
 */
export interface UseFloorPlanImageOptions {
  /**
   * CORS crossOrigin attribute for the image
   * @default 'anonymous'
   */
  crossOrigin?: 'anonymous' | 'use-credentials';

  /**
   * Referrer policy for the image request
   */
  referrerPolicy?:
    | 'no-referrer'
    | 'no-referrer-when-downgrade'
    | 'origin'
    | 'origin-when-cross-origin'
    | 'same-origin'
    | 'strict-origin'
    | 'strict-origin-when-cross-origin'
    | 'unsafe-url';
}

/**
 * Return value from useFloorPlanImage hook
 */
export interface UseFloorPlanImageResult {
  /** The loaded HTMLImageElement, or undefined if not loaded */
  image: HTMLImageElement | undefined;

  /** Current loading status */
  status: ImageLoadingStatus;

  /** True while the image is loading */
  isLoading: boolean;

  /** True when the image has successfully loaded */
  isLoaded: boolean;

  /** True if the image failed to load */
  hasError: boolean;

  /** Natural width of the image in pixels (0 if not loaded) */
  naturalWidth: number;

  /** Natural height of the image in pixels (0 if not loaded) */
  naturalHeight: number;

  /** Aspect ratio (width / height), or 0 if not loaded */
  aspectRatio: number;
}

/**
 * Hook for loading floor plan images with Konva
 *
 * This hook wraps the use-image library to provide a more convenient API
 * for loading images to use with Konva's Image component.
 *
 * Features:
 * - Automatic CORS handling for Firebase Storage URLs
 * - Convenient boolean flags for loading states
 * - Access to image dimensions once loaded
 * - Aspect ratio calculation
 *
 * @param url - URL of the floor plan image to load
 * @param options - Optional configuration for CORS and referrer policy
 * @returns Object containing the image and loading state information
 *
 * @example
 * ```tsx
 * import { Image as KonvaImage } from 'react-konva';
 * import { useFloorPlanImage } from '@/hooks/useFloorPlanImage';
 *
 * const FloorPlanBackground = ({ imageUrl }: { imageUrl: string }) => {
 *   const { image, isLoaded, naturalWidth, naturalHeight } = useFloorPlanImage(imageUrl);
 *
 *   if (!isLoaded) return null;
 *
 *   return <KonvaImage image={image} width={naturalWidth} height={naturalHeight} />;
 * };
 * ```
 */
export const useFloorPlanImage = (
  url: string,
  options: UseFloorPlanImageOptions = {}
): UseFloorPlanImageResult => {
  const { crossOrigin = 'anonymous', referrerPolicy } = options;
  const loadStartRef = useRef<boolean>(false);

  const [image, status] = useImage(url, crossOrigin, referrerPolicy);

  const isLoading = status === 'loading';
  const isLoaded = status === 'loaded';
  const hasError = status === 'failed';

  // Track image loading performance
  useEffect(() => {
    if (isLoading && !loadStartRef.current) {
      loadStartRef.current = true;
      perfStart('imageLoad');
    }
    if ((isLoaded || hasError) && loadStartRef.current) {
      perfEnd('imageLoad');
      loadStartRef.current = false;
    }
  }, [isLoading, isLoaded, hasError]);

  // Get natural dimensions from the image element
  const naturalWidth = image?.naturalWidth ?? 0;
  const naturalHeight = image?.naturalHeight ?? 0;

  // Calculate aspect ratio (width / height)
  // Return 0 if height is 0 to avoid division by zero
  const aspectRatio = naturalHeight > 0 ? naturalWidth / naturalHeight : 0;

  return {
    image,
    status,
    isLoading,
    isLoaded,
    hasError,
    naturalWidth,
    naturalHeight,
    aspectRatio,
  };
};
